import {
	forwardRef,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useState
} from "react";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration, IRootValidation } from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import {
	initialMaintenanceWindowState,
	maintenanceWindowValidator,
	prepareMaintenanceWindowForSaving
} from "../MaintenanceWindow";
import {
	conditionalAccessValidator,
	initialConditionalAccessState,
	prepareConditionalAccessForSaving
} from "../ConditionalAccess";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import { AppType, ClientlessRdpDestinationWindow } from "client/Api/Apps/common";
import { EditAppHandle } from "../EditApp";
import {
	FormItem,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import {
	getClientlessRdpGateways,
	ClientlessRdpGatewayOptionModel,
	getClientlessRdpConnections,
	ClientlessRdpConnectionOptionModel
} from "client/Api/Apps/EditApp/appParameters";
import { isNil, isNotNil } from "client/Misc/utils";
import { useSynchronizedRef } from "client/Hooks";
import {
	EditClientlessRdpAppViewModel,
	saveClientlessRdpApp,
	SaveClientlessRdpAppModel
} from "client/Api/Apps/EditApp/saveApp";

export type EditClientlessRdpAppProps = {
	initialSettings: EditAppSettings<EditClientlessRdpAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditClientlessRdpApp = forwardRef(
	(
		props: EditClientlessRdpAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveClientlessRdpAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const [gatewayOptions, setGatewayOptions] = useState<readonly SelectItem<number>[]>([]);
		const [connectionOptions, setConnectionOptions] = useState<readonly SelectItem<string>[]>([]);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const { canBeConfigured } = app;

		const formHandler: IFormHandler<SaveClientlessRdpAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const validationRef: ReadonlyRefObject<IRootValidation<SaveClientlessRdpAppModel>> =
			useSynchronizedRef<IRootValidation<SaveClientlessRdpAppModel>>(formHandler.validation);

		useEffect(
			(): (() => void) => {
				async function fetchGateways(signal: AbortSignal): Promise<void> {
					try {
						const data: ClientlessRdpGatewayOptionModel[] = await getClientlessRdpGateways(
							signal
						);

						setGatewayOptions(
							data.map(x => ({
								value: x.id,
								text: x.name
							}))
						);

						if (data.length > 0) {
							setFormData(
								(formData) => {
									if (isNotNil(formData.gatewayId)) {
										return formData;
									}

									return {
										...formData,
										gatewayId: data[0].id
									};
								}
							);

							validationRef.current.for("gatewayId")
								.triggerInputFinished();
						}
					} catch(e) {
						if (isCancelledError(e)) {
							return;
						}

						throw e;
					}
				};

				const cancellation = new AbortController();

				fetchGateways(cancellation.signal);

				return () => cancellation.abort();
			},
			[validationRef]
		);

		useEffect(
			(): (() => void) | undefined => {
				let gatewayId: number;

				if (isNil(formData.gatewayId)) {
					return undefined;
				} else {
					gatewayId = formData.gatewayId;
				}

				async function fetchConnections(signal: AbortSignal): Promise<void> {
					try {
						const data: ClientlessRdpConnectionOptionModel[] = await getClientlessRdpConnections(
							gatewayId,
							signal
						);

						setConnectionOptions(
							data.map(x => ({
								value: x.id,
								text: x.name
							}))
						);

						if (data.length > 0) {
							setFormData(
								(formData) => {
									if (isNotNil(formData.connectionId)) {
										return formData;
									}

									return {
										...formData,
										connectionId: data[0].id
									};
								}
							);

							validationRef.current.for("connectionId")
								.triggerInputFinished();
						}
					} catch(e) {
						if (isCancelledError(e)) {
							return;
						}

						throw e;
					}
				};

				const cancellation = new AbortController();

				fetchConnections(cancellation.signal);

				return () => cancellation.abort();
			},
			[formData.gatewayId, validationRef]
		);

		const prepareModelForSaving = useCallback(
			(model: SaveClientlessRdpAppModel): SaveClientlessRdpAppModel => {
				return {
					...model,
					gatewayId: canBeConfigured ? model.gatewayId : null,
					connectionId: canBeConfigured ? model.connectionId : null,
					maintenanceSettings: prepareMaintenanceWindowForSaving(model.maintenanceSettings),
					conditionalAccessSettings: prepareConditionalAccessForSaving(model.conditionalAccessSettings)
				};
			},
			[canBeConfigured]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveClientlessRdpAppModel>(
			formHandler,
			saveClientlessRdpApp,
			AppType.ClientlessRdp,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under(
				"gatewayId",
				"connectionId",
				"destinationWindow"
			)
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				{canBeConfigured && (
					<>
						<FormItem label={localize("_Gateway")}>
							<Select
								{...formHandler.for("gatewayId")
									.attachToSelect()
								}
								options={gatewayOptions}
								menuTitle={localize("_Gateway")}
							/>
							<ValidationError
								{...formHandler
									.for("gatewayId")
									.attachToValidationError()}
							/>
						</FormItem>
						<FormItem label={localize("_Connection")}>
							<Select
								{...formHandler.for("connectionId")
									.attachToSelect()
								}
								options={connectionOptions}
								menuTitle={localize("_Connection")}
							/>
							<ValidationError
								{...formHandler
									.for("connectionId")
									.attachToValidationError()}
							/>
						</FormItem>
						<FormItem label={localize("_Destination")}>
							<Select
								{...formHandler.for("destinationWindow")
									.attachToSelect()
								}
								options={destinationWindowOptions}
								menuTitle={localize("_Destination")}
							/>
							<ValidationError
								{...formHandler.for("destinationWindow")
									.attachToValidationError()
								}
							/>
						</FormItem>
					</>
				)}
			</EditAppBase>
		);
	}
);
EditClientlessRdpApp.displayName = "EditClientlessRdpApp";


function convertToSaveModel(
	initialSettings: EditAppSettings<EditClientlessRdpAppViewModel>
): SaveClientlessRdpAppModel {
	const {
		gatewayId,
		connectionId,
		destinationWindow,
		maintenanceWindow,
		conditionalAccessSettings
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		destinationWindow,
		gatewayId,
		connectionId,
		maintenanceSettings: initialMaintenanceWindowState(maintenanceWindow),
		conditionalAccessSettings: initialConditionalAccessState(conditionalAccessSettings)
	};
}

const validation = createValidationConfiguration<SaveClientlessRdpAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forObject("conditionalAccessSettings")
	.addValidators(conditionalAccessValidator());

validation.forObject("maintenanceSettings")
	.addValidators(maintenanceWindowValidator());

const destinationWindowOptions: SelectItem<ClientlessRdpDestinationWindow>[] = [
	{ text: localize("_NewWindow"), value: ClientlessRdpDestinationWindow.NewWindow },
	{ text: localize("_NewPopOutWindow"), value: ClientlessRdpDestinationWindow.NewPopOutWindow },
];